import React, { useState } from 'react';

import type { DebugStep } from '@captivateiq/assistant/types';
import { QBox, QStack, QText } from '@captivateiq/quotient';

const COLLAPSE_LINE_THRESHOLD = 5;
const COLLAPSE_CHAR_THRESHOLD = 200;

const CollapsibleBlock: React.FC<{ children: string; fadeColor?: string }> = ({
  children,
  fadeColor = 'background.paper',
}) => {
  const lines = children.split('\n');
  const shouldCollapse = lines.length > COLLAPSE_LINE_THRESHOLD || children.length > COLLAPSE_CHAR_THRESHOLD;
  const [expanded, setExpanded] = useState(!shouldCollapse);

  const collapseLabel =
    lines.length > COLLAPSE_LINE_THRESHOLD ? `Show all (${lines.length} lines)` : `Show all (${children.length} chars)`;

  return (
    <QBox>
      <QBox sx={{ position: 'relative' }}>
        <QBox
          component="pre"
          sx={{
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '11.5px',
            lineHeight: 1.5,
            maxHeight: expanded ? 'none' : '80px',
            overflow: 'hidden',
          }}
        >
          {children}
        </QBox>
        {shouldCollapse && !expanded && (
          <QBox
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '32px',
              backgroundImage: (theme) => {
                const parts = fadeColor.split('.');
                let color: unknown = theme.palette;
                for (const p of parts) color = (color as Record<string, unknown>)?.[p] ?? color;
                return `linear-gradient(to bottom, transparent, ${typeof color === 'string' ? color : '#fff'})`;
              },
              pointerEvents: 'none',
            }}
          />
        )}
      </QBox>
      {shouldCollapse && (
        <QText
          sx={{
            fontSize: '10px',
            fontWeight: 600,
            color: 'primary.main',
            cursor: 'pointer',
            mt: 0.5,
            '&:hover': { textDecoration: 'underline' },
          }}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? 'Collapse' : collapseLabel}
        </QText>
      )}
    </QBox>
  );
};

const LlmStep: React.FC<{ step: Extract<DebugStep, { type: 'llm' }>; index: number }> = ({ step, index }) => (
  <QBox
    sx={{
      border: '1px dashed',
      borderColor: 'secondary.main',
      borderRadius: '8px',
      padding: '10px 14px',
      backgroundColor: 'secondary.50',
      fontFamily: '"SF Mono", "Cascadia Code", Menlo, monospace',
    }}
  >
    <QText
      sx={{
        fontSize: '10px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'secondary.main',
        mb: 0.5,
      }}
    >
      LLM Step {index}
    </QText>
    <CollapsibleBlock fadeColor="secondary.50">{step.text}</CollapsibleBlock>
  </QBox>
);

const ToolCallStep: React.FC<{ step: Extract<DebugStep, { type: 'tool_call' }> }> = ({ step }) => (
  <QBox
    sx={{
      border: '1px solid',
      borderColor: 'success.main',
      borderRadius: '6px',
      padding: '8px 12px',
      backgroundColor: 'success.50',
      fontFamily: '"SF Mono", "Cascadia Code", Menlo, monospace',
      fontSize: '11px',
    }}
  >
    <QText
      sx={{
        fontSize: '10px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'success.main',
        mb: 0.5,
      }}
    >
      Tool: {step.name}
    </QText>
    <CollapsibleBlock fadeColor="success.50">{JSON.stringify(step.args, null, 2)}</CollapsibleBlock>
  </QBox>
);

const ToolResultStep: React.FC<{ step: Extract<DebugStep, { type: 'tool_result' }> }> = ({ step }) => (
  <QBox
    sx={{
      border: '1px solid',
      borderColor: 'success.main',
      borderRadius: '6px',
      padding: '8px 12px',
      backgroundColor: 'success.50',
      fontFamily: '"SF Mono", "Cascadia Code", Menlo, monospace',
      fontSize: '11px',
    }}
  >
    <QText
      sx={{
        fontSize: '10px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'success.main',
        mb: 0.5,
      }}
    >
      Result: {step.name}
    </QText>
    <CollapsibleBlock fadeColor="success.50">{step.output}</CollapsibleBlock>
  </QBox>
);

type DebugStepsProps = {
  steps: DebugStep[];
};

export const DebugSteps: React.FC<DebugStepsProps> = ({ steps }) => {
  let llmIndex = 0;
  let toolCallIndex = 0;
  let toolResultIndex = 0;

  return (
    <QStack spacing={1} sx={{ my: 1 }}>
      {steps.map((step) => {
        if (step.type === 'llm') {
          llmIndex += 1;
          return <LlmStep index={llmIndex} key={`llm-${llmIndex}`} step={step} />;
        }
        if (step.type === 'tool_call') {
          toolCallIndex += 1;
          return <ToolCallStep key={`tc-${toolCallIndex}`} step={step} />;
        }
        if (step.type === 'tool_result') {
          toolResultIndex += 1;
          return <ToolResultStep key={`tr-${toolResultIndex}`} step={step} />;
        }
        return null;
      })}
    </QStack>
  );
};
